import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "reachos.access_token";
const REFRESH_KEY = "reachos.refresh_token";

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  setRefresh: (t) => t ? localStorage.setItem(REFRESH_KEY, t) : localStorage.removeItem(REFRESH_KEY),
  clear: () => { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(REFRESH_KEY); },
};

const client = axios.create({ baseURL: API, headers: { "Content-Type": "application/json" } });

// Attach Bearer token from localStorage on every request (works around CORS-credentials proxy issues)
client.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const api = {
  // accounts
  listAccounts: () => client.get("/accounts").then(r => r.data),
  createAccount: (data) => client.post("/accounts", data).then(r => r.data),
  updateAccount: (id, data) => client.patch(`/accounts/${id}`, data).then(r => r.data),
  deleteAccount: (id) => client.delete(`/accounts/${id}`).then(r => r.data),
  // hooks
  listHooks: () => client.get("/hooks").then(r => r.data),
  createHook: (data) => client.post("/hooks", data).then(r => r.data),
  updateHook: (id, data) => client.patch(`/hooks/${id}`, data).then(r => r.data),
  deleteHook: (id) => client.delete(`/hooks/${id}`).then(r => r.data),
  // captions
  listCaptions: () => client.get("/captions").then(r => r.data),
  createCaption: (data) => client.post("/captions", data).then(r => r.data),
  deleteCaption: (id) => client.delete(`/captions/${id}`).then(r => r.data),
  // posts
  listPosts: () => client.get("/posts").then(r => r.data),
  createPost: (data) => client.post("/posts", data).then(r => r.data),
  updatePost: (id, data) => client.patch(`/posts/${id}`, data).then(r => r.data),
  deletePost: (id) => client.delete(`/posts/${id}`).then(r => r.data),
  // competitors
  listCompetitors: () => client.get("/competitors").then(r => r.data),
  createCompetitor: (data) => client.post("/competitors", data).then(r => r.data),
  deleteCompetitor: (id) => client.delete(`/competitors/${id}`).then(r => r.data),
  // kpis
  listKpis: () => client.get("/kpis").then(r => r.data),
  createKpi: (data) => client.post("/kpis", data).then(r => r.data),
  // ai
  aiHooks: (data) => client.post("/ai/generate-hooks", data).then(r => r.data),
  aiCaption: (data) => client.post("/ai/generate-caption", data).then(r => r.data),
  aiVideoIdeas: (data) => client.post("/ai/generate-video-ideas", data).then(r => r.data),
  aiAnalyze: (data) => client.post("/ai/analyze-performance", data).then(r => r.data),
  aiSpy: (data) => client.post("/ai/spy-competitor", data).then(r => r.data),
  aiDeepDive: (data) => client.post("/ai/deep-dive", data).then(r => r.data),
  aiScoreVirality: (data) => client.post("/ai/score-virality", data).then(r => r.data),
  // trends
  listTrends: () => client.get("/trends").then(r => r.data),
  createTrend: (data) => client.post("/trends", data).then(r => r.data),
  updateTrend: (id, data) => client.patch(`/trends/${id}`, data).then(r => r.data),
  deleteTrend: (id) => client.delete(`/trends/${id}`).then(r => r.data),
  // candidates (VA recruiting)
  listCandidates: () => client.get("/candidates").then(r => r.data),
  submitCandidate: (data) => client.post("/candidates", data).then(r => r.data),
  deleteCandidate: (id) => client.delete(`/candidates/${id}`).then(r => r.data),
  // workspaces
  listWorkspaces: () => client.get("/workspaces").then(r => r.data),
  createWorkspace: (data) => client.post("/workspaces", data).then(r => r.data),
  updateWorkspace: (id, data) => client.patch(`/workspaces/${id}`, data).then(r => r.data),
  deleteWorkspace: (id) => client.delete(`/workspaces/${id}`).then(r => r.data),
  // goals
  listGoals: () => client.get("/goals").then(r => r.data),
  createGoal: (data) => client.post("/goals", data).then(r => r.data),
  updateGoal: (id, data) => client.patch(`/goals/${id}`, data).then(r => r.data),
  deleteGoal: (id) => client.delete(`/goals/${id}`).then(r => r.data),
  // missions
  listMissions: () => client.get("/missions").then(r => r.data),
  createMission: (data) => client.post("/missions", data).then(r => r.data),
  updateMission: (id, data) => client.patch(`/missions/${id}`, data).then(r => r.data),
  deleteMission: (id) => client.delete(`/missions/${id}`).then(r => r.data),
  seedTodayMissions: (workspace_id = "") => client.post(`/missions/seed-today?workspace_id=${workspace_id}`).then(r => r.data),
  // ideas
  listIdeas: () => client.get("/ideas").then(r => r.data),
  createIdea: (data) => client.post("/ideas", data).then(r => r.data),
  updateIdea: (id, data) => client.patch(`/ideas/${id}`, data).then(r => r.data),
  deleteIdea: (id) => client.delete(`/ideas/${id}`).then(r => r.data),
  // AI contextual + content analyzer
  aiChat: (data) => client.post("/ai/contextual-chat", data).then(r => r.data),
  aiAnalyzeContent: (data) => client.post("/ai/analyze-content", data).then(r => r.data),
  aiAutoNotes: (data) => client.post("/ai/auto-notes", data).then(r => r.data),
  aiPostingPlan: (data) => client.post("/ai/posting-plan", data).then(r => r.data),
  aiABVariants: (data) => client.post("/ai/ab-variants", data).then(r => r.data),
  aiCompetitorAnalyze: (data) => client.post("/ai/competitor-analyze", data).then(r => r.data),
  // swipe file
  listSwipe: () => client.get("/swipe").then(r => r.data),
  createSwipe: (data) => client.post("/swipe", data).then(r => r.data),
  updateSwipe: (id, data) => client.patch(`/swipe/${id}`, data).then(r => r.data),
  deleteSwipe: (id) => client.delete(`/swipe/${id}`).then(r => r.data),
  // posting plans
  listPostingPlans: () => client.get("/posting-plans").then(r => r.data),
  createPostingPlan: (data) => client.post("/posting-plans", data).then(r => r.data),
  deletePostingPlan: (id) => client.delete(`/posting-plans/${id}`).then(r => r.data),
  // auth
  authLogin: (data) => client.post("/auth/login", data).then(r => r.data),
  authLogout: () => client.post("/auth/logout").then(r => r.data),
  authMe: () => client.get("/auth/me").then(r => r.data),
  authRegister: (data) => client.post("/auth/register", data).then(r => r.data),
  authRefresh: () => client.post("/auth/refresh").then(r => r.data),
  authTeam: () => client.get("/auth/team").then(r => r.data),
  authAdminCreate: (data) => client.post("/auth/admin/create", data).then(r => r.data),
  authActivate: (id) => client.post(`/auth/team/${id}/activate`).then(r => r.data),
  authToggleActive: (id) => client.patch(`/auth/team/${id}/toggle-active`).then(r => r.data),
  authDeleteUser: (id) => client.delete(`/auth/team/${id}`).then(r => r.data),
  authUpdateRole: (id, role) => client.patch(`/auth/team/${id}/role`, { role }).then(r => r.data),

  // === CRM Models ===
  crmListModels: () => client.get("/crm/models").then(r => r.data),
  crmCreateModel: (data) => client.post("/crm/models", data).then(r => r.data),
  crmGetModel: (id) => client.get(`/crm/models/${id}`).then(r => r.data),
  crmUpdateModel: (id, data) => client.patch(`/crm/models/${id}`, data).then(r => r.data),
  crmDeleteModel: (id) => client.delete(`/crm/models/${id}`).then(r => r.data),

  // === IG Accounts ===
  igListAccounts: () => client.get("/instagram/accounts").then(r => r.data),
  igCreateAccount: (data) => client.post("/instagram/accounts", data).then(r => r.data),
  igUpdateAccount: (id, data) => client.patch(`/instagram/accounts/${id}`, data).then(r => r.data),
  igDeleteAccount: (id) => client.delete(`/instagram/accounts/${id}`).then(r => r.data),
  igOauthStatus: () => client.get("/instagram/oauth/status").then(r => r.data),
  igOauthStart: (accountId) => client.get(`/instagram/oauth/start?account_id=${accountId}`).then(r => r.data),
  igSync: (id) => client.post(`/instagram/accounts/${id}/sync`).then(r => r.data),

  // === IG Stats ===
  igStats: (id, days = 90) => client.get(`/instagram/stats/${id}?days=${days}`).then(r => r.data),
  igStatsSummary: (id) => client.get(`/instagram/stats-summary/${id}`).then(r => r.data),
  igAddManualStats: (data) => client.post("/instagram/stats/manual", data).then(r => r.data),

  // === IG Media ===
  igListMedia: (accountId) => client.get(`/instagram/media${accountId ? `?account_id=${accountId}` : ""}`).then(r => r.data),
  igCreateMedia: (data) => client.post("/instagram/media", data).then(r => r.data),
  igUpdateMedia: (id, data) => client.patch(`/instagram/media/${id}`, data).then(r => r.data),
  igDeleteMedia: (id) => client.delete(`/instagram/media/${id}`).then(r => r.data),

  // === CRM Notes / Tasks / Goals / Alerts ===
  crmListNotes: (params = {}) => client.get("/crm/notes", { params }).then(r => r.data),
  crmCreateNote: (data) => client.post("/crm/notes", data).then(r => r.data),
  crmDeleteNote: (id) => client.delete(`/crm/notes/${id}`).then(r => r.data),

  crmListTasks: (params = {}) => client.get("/crm/tasks", { params }).then(r => r.data),
  crmCreateTask: (data) => client.post("/crm/tasks", data).then(r => r.data),
  crmUpdateTask: (id, data) => client.patch(`/crm/tasks/${id}`, data).then(r => r.data),
  crmDeleteTask: (id) => client.delete(`/crm/tasks/${id}`).then(r => r.data),

  crmListCrmGoals: (params = {}) => client.get("/crm/crm-goals", { params }).then(r => r.data),
  crmCreateCrmGoal: (data) => client.post("/crm/crm-goals", data).then(r => r.data),
  crmUpdateCrmGoal: (id, data) => client.patch(`/crm/crm-goals/${id}`, data).then(r => r.data),
  crmDeleteCrmGoal: (id) => client.delete(`/crm/crm-goals/${id}`).then(r => r.data),

  crmListAlerts: () => client.get("/crm/alerts").then(r => r.data),
  crmReadAlert: (id) => client.patch(`/crm/alerts/${id}/read`).then(r => r.data),
  crmResolveAlert: (id) => client.patch(`/crm/alerts/${id}/resolve`).then(r => r.data),
  crmDeleteAlert: (id) => client.delete(`/crm/alerts/${id}`).then(r => r.data),
  crmScanAlerts: () => client.post("/crm/alerts/scan").then(r => r.data),

  crmActivity: (limit = 100) => client.get(`/crm/activity?limit=${limit}`).then(r => r.data),
  crmListPermissions: (accountId) => client.get(`/crm/permissions/${accountId}`).then(r => r.data),
  crmGrantPermission: (data) => client.post("/crm/permissions/grant", data).then(r => r.data),
  crmRevokePermission: (id) => client.delete(`/crm/permissions/${id}`).then(r => r.data),

  // === SOCIAL CRM Contacts / Segments ===
  crmListContacts: (params = {}) => client.get("/crm/contacts", { params }).then(r => r.data),
  crmCreateContact: (data) => client.post("/crm/contacts", data).then(r => r.data),
  crmUpdateContact: (id, data) => client.patch(`/crm/contacts/${id}`, data).then(r => r.data),
  crmDeleteContact: (id) => client.delete(`/crm/contacts/${id}`).then(r => r.data),
  crmListSegments: () => client.get("/crm/segments").then(r => r.data),
  crmCreateSegment: (data) => client.post("/crm/segments", data).then(r => r.data),
  crmDeleteSegment: (id) => client.delete(`/crm/segments/${id}`).then(r => r.data),
  // seed
  seed: () => client.post("/seed").then(r => r.data),
};

export default api;
